import { motion } from 'framer-motion';
import { Palette, Wand2, Sparkles, Image as ImageIcon, Loader2, Download, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AIArtFormProps {
  contentData: Record<string, string>;
  onUpdate: (data: Record<string, string>) => void;
}

export function AIArtForm({ contentData, onUpdate }: AIArtFormProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [resultImage, setResultImage] = useState<string | null>(contentData.aiImageUrl || null);

  const styles = [
    { id: 'cyberpunk', label: 'Cyberpunk', icon: '🌃' },
    { id: 'anime', label: 'Anime', icon: '⛩️' },
    { id: 'oil-painting', label: 'Oil Painting', icon: '🎨' },
    { id: 'minimalist', label: 'Minimalist', icon: '⚪' },
    { id: 'futuristic', label: 'Futuristic', icon: '🚀' },
    { id: 'vintage', label: 'Vintage', icon: '📻' },
  ];

  const handleGenerate = async () => {
    if (!contentData.url || !contentData.prompt) {
      toast.error('Please provide a URL and a prompt');
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('artistic-ai-qr', {
        body: { 
          url: contentData.url, 
          prompt: contentData.prompt, 
          style: contentData.style || 'cyberpunk' 
        }
      });

      if (error) throw error;

      if (data?.imageUrl) {
        setResultImage(data.imageUrl);
        onUpdate({ ...contentData, aiImageUrl: data.imageUrl });
        toast.success('AI Artwork generated!');
      }
    } catch (error: any) {
      console.error('AI Generation error:', error);
      toast.error(error.message || 'Failed to generate AI artwork');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center gap-2">
        <Palette className="h-5 w-5 text-primary" />
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          AI Artistic QR
        </h3>
      </div>

      <Card className="p-4 space-y-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Destination URL</Label>
            <Input 
              placeholder="https://example.com" 
              value={contentData.url || ''} 
              onChange={e => onUpdate({ ...contentData, url: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Artistic Prompt</Label>
            <Textarea 
              placeholder="Describe the art style (e.g., 'A futuristic city with neon lights, digital art style')" 
              value={contentData.prompt || ''} 
              onChange={e => onUpdate({ ...contentData, prompt: e.target.value })}
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <Label>Art Style</Label>
            <div className="grid grid-cols-3 gap-2">
              {styles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => onUpdate({ ...contentData, style: style.id })}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all ${
                    contentData.style === style.id
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border/50 hover:border-primary/50'
                  }`}
                >
                  <span className="text-2xl mb-1">{style.icon}</span>
                  <span className="text-[10px] font-medium">{style.label}</span>
                </button>
              ))}
            </div>
          </div>

          <Button 
            className="w-full gradient-hero gap-2 py-6" 
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating Art...
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4" />
                Generate AI Artwork
              </>
            )}
          </Button>
          
          <p className="text-[10px] text-center text-muted-foreground italic">
            <Sparkles className="h-3 w-3 inline mr-1" />
            AI generation will create a scannable artwork based on your prompt.
          </p>
        </div>
      </Card>

      {resultImage && (
        <Card className="p-4 border-primary/20 bg-primary/5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-primary flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Result
            </h4>
            <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1" asChild>
              <a href={resultImage} download="ai-qr.png">
                <Download className="h-3 w-3" /> Download
              </a>
            </Button>
          </div>
          <div className="aspect-square bg-white rounded-lg overflow-hidden border shadow-inner flex items-center justify-center">
            <img src={resultImage} alt="AI Generated QR" className="w-full h-full object-cover" />
          </div>
        </Card>
      )}

      <div className="pt-4">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Example Results</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="aspect-square bg-muted rounded-xl flex items-center justify-center border-2 border-dashed border-muted-foreground/10 overflow-hidden">
            <ImageIcon className="h-8 w-8 text-muted-foreground/20" />
          </div>
          <div className="aspect-square bg-muted rounded-xl flex items-center justify-center border-2 border-dashed border-muted-foreground/10 overflow-hidden">
            <ImageIcon className="h-8 w-8 text-muted-foreground/20" />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
